import React, { useEffect } from 'react';
import styles from './App.module.scss';
import Modal from 'react-modal';
import '../../i18n/config';
import { BrowserRouter as Router, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Authorization from '../Authorization/Authorization';
import { useDispatch } from 'react-redux';
import TokenService from '../../services/token.service';
import { isExpired } from 'react-jwt';
import { useAppSelector } from '../../config/redux/hooks';
import { getMe } from './appSlice';
import ScreenShadow from '../AdminDashboard/Features/ScreenShadow/ScreenShadow';
import SignUpByInvitePage from '../Authorization/Pages/SignUpByInvitePage/SignUpByInvitePage';
import AppRoutes from './AppRoutes';
import { UserRole } from '../../common/enums/UserRole';

const Onboarding = React.lazy(() => import('../Onboarding/Onboarding'));
const Dashboard = React.lazy(() => import('../MembersDashboard/MembersDashboard'));
const AdminDashboard = React.lazy(() => import('../AdminDashboard/AdminDashboard'));

Modal.setAppElement('#root');
function App() {
  return (
    <Router>
      <div className={styles.app}>
        <AppNavigation />
      </div>
    </Router>
  );
}

function AppNavigation() {
  const navigation = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const loggingPending = useAppSelector(state => state.auth.basic.pending);
  const loggedOut = useAppSelector(state => state.auth.basic.loggedOut);
  const userInfo = useAppSelector(state => state.app.base.userInfo);

  // auth redirection effect
  useEffect(() => {
    // Ignore redirection rules if user on auth stage
    if (location.pathname.startsWith('/auth')) {
      return;
    }
    // Ignore redirection rules if user invited
    if (location.pathname.startsWith('/invites')) {
      return;
    }

    const isRefreshExpired = isExpired(TokenService.getLocalRefreshToken());
    // Redirect to login page if:
    // 1) first open
    // 2) if refresh token expired
    // 3) if user logged out
    if (isRefreshExpired || loggedOut) {
      navigation('/auth');
      return;
    }
  }, [loggingPending, loggedOut]);

  // get user after authorization
  useEffect(() => {
    if (!userInfo) {
      const token = TokenService.getLocalAccessToken();
      const isExpiredToken = isExpired(token);
      if (!isExpiredToken) {
        dispatch(getMe(null));
      }
    }
  }, [loggingPending]);

  // app redirections effect
  useEffect(() => {
    if (!userInfo) return;

    // Ignore redirection rules if user invited
    if (location.pathname.startsWith('/invites')) {
      return;
    }

    // for dev
    // if (location.pathname.startsWith('/auth')) {
    //   return;
    // }

    // loop redirection quick fix
    const token = TokenService.getLocalAccessToken();
    const isExpiredToken = isExpired(token);
    if (isExpiredToken) {
      return;
    }

    const isAdmin = userInfo.role === UserRole.ADMIN;
    if (isAdmin) {
      if (location.pathname.startsWith('/admin-dashboard')) {
        return;
      }
      navigation('/admin-dashboard');
      return;
    }

    // Redirect to onboarding if user is not complete it
    if (!userInfo.professionalId) {
      // Do not redirect if user already on onboarding
      if (location.pathname.startsWith('/onboarding')) {
        return;
      }
      navigation('/onboarding');
      return;
    }

    // Ignore redirection if user already on dashboard
    if (location.pathname.startsWith('/subscriptions')) {
      return;
    }

    // Ignore redirection if user already on dashboard
    if (location.pathname.startsWith('/profile')) {
      return;
    }

    // Redirect to home if user have professional id
    // if (userInfo.professionalId) {
    navigation('/');
    // }
  }, [userInfo]);

  return (
    <Routes>
      <Route path={`/${AppRoutes.AUTH_PATH}/*`} element={<Authorization />} />
      <Route path={`/${AppRoutes.INVITES_PATH}/:token`} element={<SignUpByInvitePage />} />
      <Route
        path={'/*'}
        element={
          <React.Suspense fallback={<ScreenShadow />}>
            <Dashboard />
          </React.Suspense>
        }
      />
      <Route
        path={`/${AppRoutes.ONBOARDING_PATH}/*`}
        element={
          <React.Suspense fallback={<ScreenShadow />}>
            <Onboarding />
          </React.Suspense>
        }
      />
      <Route
        path={`/${AppRoutes.ADMIN_DASHBOARD_PATH}/*`}
        element={
          <React.Suspense fallback={<ScreenShadow />}>
            <AdminDashboard />
          </React.Suspense>
        }
      />
    </Routes>
  );
}

export default App;
